import React, { useState, useEffect } from "react";
import axios from "../../services/api";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import VendorForm from "./VendorForm";
import "bootstrap/dist/css/bootstrap.min.css";

const VendorList = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [editVendor, setEditVendor] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchVendors();
  }, [page, pageSize, searchQuery]);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/vendors?page=${page}&limit=${pageSize}&search=${searchQuery}`);
    
      console.log("API Response:", response.data); // ✅ Debugging API response structure
  
      if (response.data && response.data.vendors) {
        setVendors(response.data.vendors); // ✅ Set vendors array
        setTotalPages(response.data.totalPages || 1); // ✅ Set total pages from API response
      } else {
        setVendors([]); 
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
    setLoading(false);
  };
  
  

  const handleEdit = (vendor) => {
    setEditVendor(vendor);
    setShowForm(true);
  };

  const handleDisable = async (id) => {
    if (!id) return console.error("Error: Vendor ID is undefined", id);

    if (window.confirm("Are you sure you want to disable this vendor?")) {
      try {
        await axios.put(`/vendors/${id}/disable`);
        fetchVendors();
      } catch (error) {
        console.error("Error disabling vendor:", error.response ? error.response.data : error);
      }
    }
  };

  const handleEnable = async (id) => {
    if (!id) return console.error("Error: Vendor ID is undefined", id);

    if (window.confirm("Are you sure you want to enable this vendor?")) {
      try {
        await axios.put(`/vendors/${id}/enable`);
        fetchVendors();
      } catch (error) {
        console.error("Error enabling vendor:", error);
      }
    }
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(vendors);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Vendors");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" });
    saveAs(data, "vendors.xlsx");
  };
  const formatDate = (isoDate) => {
    if (!isoDate) return "N/A"; // Handle null or undefined values
    const date = new Date(isoDate);
    return date.toLocaleDateString("en-GB"); // Formats as dd/mm/yyyy
  };
  return (
    <div className="mt-1">
      <h2 className="mb-3">Vendors</h2>

      <button
        onClick={() => {
          setShowForm(true);
          setEditVendor(null);
        }}
        className="btn btn-primary mb-3"
      >
        + Add Vendor
      </button>

      {showForm && (
        <VendorForm
          show={showForm}
          handleClose={() => setShowForm(false)}
          fetchVendors={fetchVendors}
          editingVendor={editVendor}
        />
      )}

      <div className="row mb-3 align-items-center">
        <div className="col-md-4">
          <select
            className="form-select"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1); // ✅ Reset to first page when changing page size
            }}
          >
            <option value="10">Show 10</option>
            <option value="50">Show 50</option>
            <option value="100">Show 100</option>
          </select>
        </div>
        <div className="col-md-4">
          <input
            type="text"
            placeholder="Search vendors..."
            className="form-control"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1); // ✅ Reset to first page when searching
            }}
          />
        </div>
        <div className="col-md-4 text-md-end">
          <button onClick={exportToExcel} className="btn btn-success">
            Export Excel
          </button>
        </div>
      </div>

      {loading ? (
        <p>Loading vendors...</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover shadow-sm">
            <thead className="thead-dark">
              <tr>
                <th>Sr No</th>
                <th>Name</th>
                <th>Contact Person</th>
                <th>Phone</th>
                <th>Email</th>
        		<th>PAN/GST</th>
                <th>Added Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {vendors.length > 0 ? (
                vendors.map((vendor, index) => (
                  <tr key={vendor.id || index}>
                    <td>{(page - 1) * pageSize + index + 1}</td>
                    <td>{vendor.name}</td>
                    <td>{vendor.contact_person}</td>
                    <td>{vendor.phone}</td>
                    <td>{vendor.email}</td>
					<td>{vendor.pan_gst}</td>
                    <td>{formatDate(vendor.created_at)}</td>
                    <td>
                      {vendor.status === 1 ? (
                        <span className="badge bg-success">Enabled</span>
                      ) : (
                        <span className="badge bg-danger">Disabled</span>
                      )}
                    </td>
                    <td>
                      <button onClick={() => handleEdit(vendor)} className="btn btn-warning btn-sm me-2">
                        ✏️ 
                      </button>
                      {vendor.status === 1 ? (
                        <button onClick={() => handleDisable(vendor.id)} className="btn btn-secondary btn-sm">
                          🚫
                        </button>
                      ) : (
                        <button onClick={() => handleEnable(vendor.id)} className="btn btn-success btn-sm">
                          ✅
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center">
                    No vendors found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* ✅ Pagination Controls */}
          <div className="d-flex justify-content-between mt-3">
            <button
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page === 1}
              className="btn btn-secondary"
            >
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={page === totalPages}
              className="btn btn-secondary"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorList;
